import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Shield, CheckCircle, XCircle, AlertTriangle, 
  RefreshCcw, TrendingUp, TrendingDown, Minus,
  Users, UserCheck, Calculator, FileEdit, 
  Download, Info, Zap, Target, Lock, Unlock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Outcome Validation Admin Panel
 * 
 * The Enforcement Gate - Shows when it's safe to move from Shadow to Soft to Enforced.
 * Tracks accuracy of all AI engines against real outcomes.
 */
const ValidationPanel = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEngine, setSelectedEngine] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    fetchOverview();
  }, [token]);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/validation/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setOverview(data.data);
      }
    } catch (err) {
      setError('Error connecting to Validation Engine');
    }
    setLoading(false);
  };

  const exportData = async (format = 'json') => {
    setExportLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/validation/export?engine=all&days=30&format=${format}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      
      if (data.success) {
        // Download file
        const blob = new Blob(
          [format === 'csv' ? data.data : JSON.stringify(data.data, null, 2)],
          { type: format === 'csv' ? 'text/csv' : 'application/json' }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `validation_export_${new Date().toISOString().split('T')[0]}.${format}`;
        a.click();
      }
    } catch (err) {
      console.error('Export error:', err);
    }
    setExportLoading(false);
  };

  const getAccuracyColor = (accuracy, meets) => {
    if (!accuracy && accuracy !== 0) return 'text-gray-400';
    if (meets) return 'text-green-400';
    if (accuracy >= 0.5) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'IMPROVING': return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'DECLINING': return <TrendingDown className="w-4 h-4 text-red-400" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getEngineIcon = (name) => {
    switch (name) {
      case 'BSS': return <Users className="w-5 h-5" />;
      case 'ARS': return <UserCheck className="w-5 h-5" />;
      case 'COMMITMENT': return <Calculator className="w-5 h-5" />;
      case 'AMENDMENT': return <FileEdit className="w-5 h-5" />;
      default: return <Shield className="w-5 h-5" />;
    }
  };

  const getEngineColor = (name) => {
    switch (name) {
      case 'BSS': return 'text-purple-400 border-purple-600/30 bg-purple-600/10';
      case 'ARS': return 'text-primary border-primary/30 bg-primary/10';
      case 'COMMITMENT': return 'text-amber-400 border-amber-600/30 bg-amber-600/10';
      case 'AMENDMENT': return 'text-rose-400 border-rose-600/30 bg-rose-600/10';
      default: return 'text-gray-400 border-gray-600/30 bg-gray-600/10';
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="p-8 text-center">
          <Shield className="w-8 h-8 animate-pulse mx-auto mb-4 text-emerald-400" />
          <p className="text-muted-foreground">Loading Validation Engine...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass-card border-red-600/30">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-4 text-red-400" />
          <p className="text-red-400">{error}</p>
          <Button onClick={fetchOverview} className="mt-4">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  const engines = overview ? [
    { key: 'bss_validation', name: 'BSS', label: 'Buyer Seriousness Score' },
    { key: 'ars_validation', name: 'ARS', label: 'Agent Risk Score' },
    { key: 'commitment_validation', name: 'COMMITMENT', label: 'Risk-Adjusted Commitment' },
    { key: 'amendment_validation', name: 'AMENDMENT', label: 'Amendment Intelligence' }
  ] : [];

  return (
    <div className="space-y-6" data-testid="validation-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display text-emerald-400 flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Outcome Validation
          </h2>
          <p className="text-sm text-muted-foreground">
            Enforcement Gate - Closed-Loop Feedback for All AI Engines
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => exportData('json')}
            disabled={exportLoading}
            className="gap-2"
          >
            <Download className="w-4 h-4" /> Export JSON
          </Button>
          <Button variant="outline" onClick={fetchOverview} className="gap-2">
            <RefreshCcw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* Enforcement Gate Status */}
      {overview && (
        <Card className={`glass-card ${
          overview.safe_to_move_to_enforced 
            ? 'border-green-600/30 bg-green-600/5' 
            : overview.safe_to_move_to_soft 
              ? 'border-yellow-600/30 bg-yellow-600/5'
              : 'border-red-600/30 bg-red-600/5'
        }`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {overview.safe_to_move_to_enforced ? (
                  <Unlock className="w-8 h-8 text-green-400" />
                ) : overview.safe_to_move_to_soft ? (
                  <Zap className="w-8 h-8 text-yellow-400" />
                ) : (
                  <Lock className="w-8 h-8 text-red-400" />
                )}
                <div>
                  <h3 className={`text-xl font-display ${
                    overview.safe_to_move_to_enforced 
                      ? 'text-green-400' 
                      : overview.safe_to_move_to_soft 
                        ? 'text-yellow-400'
                        : 'text-red-400'
                  }`}>
                    {overview.safe_to_move_to_enforced 
                      ? 'READY FOR ENFORCEMENT'
                      : overview.safe_to_move_to_soft 
                        ? 'READY FOR SOFT MODE'
                        : 'SHADOW MODE REQUIRED'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Current Mode: <span className="font-medium">{overview.current_mode}</span>
                    {' | '}
                    All Engines Meet Threshold: {overview.all_engines_meet_threshold ? '✓' : '✗'}
                    {' | '}
                    Samples Sufficient: {overview.all_samples_sufficient ? '✓' : '✗'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Threshold: 65% accuracy</p>
                <p className="text-xs text-muted-foreground">Min Sample: 30 events</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 4 Engine Tiles */}
      {overview && (
        <div className="grid grid-cols-4 gap-4">
          {engines.map(({ key, name, label }) => {
            const data = overview[key];
            if (!data) return null;
            
            const accuracy = data.accuracy_30d;
            const sample = data.sample_size_30d;
            const meets = data.meets_threshold;
            const sufficient = data.sample_sufficient_30d;
            
            return (
              <Card 
                key={name} 
                className={`glass-card cursor-pointer transition-all hover:scale-105 ${getEngineColor(name)}`}
                onClick={() => setSelectedEngine(selectedEngine === name ? null : name)}
                data-testid={`validation-tile-${name.toLowerCase()}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getEngineIcon(name)}
                      <span className="font-medium">{name}</span>
                    </div>
                    {meets ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                  
                  <div className="text-center mb-3">
                    <div className={`text-3xl font-display ${getAccuracyColor(accuracy, meets)}`}>
                      {accuracy !== null ? `${(accuracy * 100).toFixed(1)}%` : 'N/A'}
                    </div>
                    <p className="text-xs text-muted-foreground">Accuracy (30d)</p>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      n={sample} {sufficient ? '✓' : '(insufficient)'}
                    </span>
                    {getTrendIcon(data.accuracy_trend)}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Expanded Engine Detail */}
      {selectedEngine && overview && (
        <Card className="glass-card border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {getEngineIcon(selectedEngine)}
              {selectedEngine} Validation Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const keyMap = {
                'BSS': 'bss_validation',
                'ARS': 'ars_validation',
                'COMMITMENT': 'commitment_validation',
                'AMENDMENT': 'amendment_validation'
              };
              const data = overview[keyMap[selectedEngine]];
              if (!data) return null;
              
              return (
                <div className="space-y-6">
                  {/* Accuracy by Window */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-4 rounded bg-secondary/10 text-center">
                      <div className={`text-2xl font-display ${getAccuracyColor(data.accuracy_30d, data.meets_threshold)}`}>
                        {data.accuracy_30d !== null ? `${(data.accuracy_30d * 100).toFixed(1)}%` : 'N/A'}
                      </div>
                      <p className="text-xs text-muted-foreground">30 Days</p>
                      <p className="text-xs text-muted-foreground">n={data.sample_size_30d}</p>
                    </div>
                    <div className="p-4 rounded bg-secondary/10 text-center">
                      <div className={`text-2xl font-display ${getAccuracyColor(data.accuracy_60d, false)}`}>
                        {data.accuracy_60d !== null ? `${(data.accuracy_60d * 100).toFixed(1)}%` : 'N/A'}
                      </div>
                      <p className="text-xs text-muted-foreground">60 Days</p>
                      <p className="text-xs text-muted-foreground">n={data.sample_size_60d}</p>
                    </div>
                    <div className="p-4 rounded bg-secondary/10 text-center">
                      <div className={`text-2xl font-display ${getAccuracyColor(data.accuracy_90d, false)}`}>
                        {data.accuracy_90d !== null ? `${(data.accuracy_90d * 100).toFixed(1)}%` : 'N/A'}
                      </div>
                      <p className="text-xs text-muted-foreground">90 Days</p>
                      <p className="text-xs text-muted-foreground">n={data.sample_size_90d}</p>
                    </div>
                    <div className="p-4 rounded bg-secondary/10 text-center">
                      <div className={`text-2xl font-display ${getAccuracyColor(data.accuracy_lifetime, false)}`}>
                        {data.accuracy_lifetime !== null ? `${(data.accuracy_lifetime * 100).toFixed(1)}%` : 'N/A'}
                      </div>
                      <p className="text-xs text-muted-foreground">Lifetime</p>
                      <p className="text-xs text-muted-foreground">n={data.sample_size_lifetime}</p>
                    </div>
                  </div>
                  
                  {/* Cohort Metrics (for Commitment) */}
                  {data.cohort_metrics && selectedEngine === 'COMMITMENT' && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium">Cohort Analysis by Commitment Band</h4>
                      <div className="grid grid-cols-4 gap-4">
                        {Object.entries(data.cohort_metrics).map(([tier, metrics]) => (
                          <div key={tier} className="p-3 rounded bg-secondary/10">
                            <p className="text-sm font-medium text-amber-400">{tier}</p>
                            <div className="mt-2 space-y-1 text-xs">
                              <p>Total: {metrics.total}</p>
                              <p className="text-green-400">Good: {metrics.good_count} ({(metrics.completion_rate * 100).toFixed(0)}%)</p>
                              <p className="text-red-400">Bad: {metrics.bad_count} ({(metrics.bad_rate * 100).toFixed(0)}%)</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Confusion Matrix (for Amendment) */}
                  {data.confusion_matrix && selectedEngine === 'AMENDMENT' && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium">Confusion Matrix</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded bg-green-600/10 border border-green-600/20">
                          <p className="text-green-400 font-medium">True Positive</p>
                          <p className="text-2xl font-display text-green-400">{data.confusion_matrix.true_positive}</p>
                          <p className="text-xs text-muted-foreground">APPROVE → CLEAN outcome</p>
                        </div>
                        <div className="p-4 rounded bg-red-600/10 border border-red-600/20">
                          <p className="text-red-400 font-medium">False Positive</p>
                          <p className="text-2xl font-display text-red-400">{data.confusion_matrix.false_positive}</p>
                          <p className="text-xs text-muted-foreground">APPROVE → BAD outcome</p>
                        </div>
                        <div className="p-4 rounded bg-yellow-600/10 border border-yellow-600/20">
                          <p className="text-yellow-400 font-medium">False Negative</p>
                          <p className="text-2xl font-display text-yellow-400">{data.confusion_matrix.false_negative}</p>
                          <p className="text-xs text-muted-foreground">REJECT → Would be fine</p>
                        </div>
                        <div className="p-4 rounded bg-blue-600/10 border border-blue-600/20">
                          <p className="text-blue-400 font-medium">True Negative</p>
                          <p className="text-2xl font-display text-blue-400">{data.confusion_matrix.true_negative}</p>
                          <p className="text-xs text-muted-foreground">REJECT → Correct warning</p>
                        </div>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <p>
                          <span className="text-muted-foreground">False Positive Rate:</span>{' '}
                          <span className="text-red-400">{(data.confusion_matrix.false_positive_rate * 100).toFixed(1)}%</span>
                        </p>
                        <p>
                          <span className="text-muted-foreground">False Negative Rate:</span>{' '}
                          <span className="text-yellow-400">{(data.confusion_matrix.false_negative_rate * 100).toFixed(1)}%</span>
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Generic Cohort Metrics */}
                  {data.cohort_metrics && !['COMMITMENT'].includes(selectedEngine) && (
                    <div className="p-4 rounded bg-secondary/10">
                      <h4 className="text-sm font-medium mb-2">Outcome Distribution</h4>
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-lg font-display">{data.cohort_metrics.total_analyzed}</p>
                          <p className="text-xs text-muted-foreground">Total Analyzed</p>
                        </div>
                        <div>
                          <p className="text-lg font-display text-green-400">{data.cohort_metrics.good_outcomes}</p>
                          <p className="text-xs text-muted-foreground">Good Outcomes</p>
                        </div>
                        <div>
                          <p className="text-lg font-display text-red-400">{data.cohort_metrics.bad_outcomes}</p>
                          <p className="text-xs text-muted-foreground">Bad Outcomes</p>
                        </div>
                        <div>
                          <p className="text-lg font-display text-primary">{data.cohort_metrics.correct_predictions}</p>
                          <p className="text-xs text-muted-foreground">Correct Predictions</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Threshold Info */}
                  <div className="p-4 rounded bg-primary/10 border border-primary/20 flex items-start gap-3">
                    <Info className="w-5 h-5 text-primary mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-primary">Validation Threshold: {(data.threshold * 100).toFixed(0)}%</p>
                      <p className="text-muted-foreground mt-1">
                        {data.meets_threshold 
                          ? 'This engine meets the accuracy threshold and has sufficient samples.'
                          : `This engine ${!data.sample_sufficient_30d ? 'needs more samples' : 'needs higher accuracy'} before enforcement can be enabled.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Methodology Info */}
      <Card className="glass-card border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Info className="w-4 h-4" />
            Validation Methodology
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-4">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-foreground mb-2">BSS (9 Metrics)</h4>
              <ul className="space-y-1 text-xs">
                <li>• KYC Completion Speed</li>
                <li>• KYC Approval Pass Rate</li>
                <li>• Intent Commitment Follow-through</li>
                <li>• Viewing Confirmation Discipline</li>
                <li>• Viewing No-Show Rate</li>
                <li>• Time-to-First-Offer</li>
                <li>• Offer Withdrawal Rate</li>
                <li>• Counterparty Responsiveness</li>
                <li>• Deal Completion Discipline</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">ARS (9 Metrics)</h4>
              <ul className="space-y-1 text-xs">
                <li>• Response Time to Viewings</li>
                <li>• Viewing Reschedule Frequency</li>
                <li>• Offer Handling Integrity</li>
                <li>• Negotiation Volatility</li>
                <li>• Deal Timeline Discipline</li>
                <li>• Amendment Pressure Rate</li>
                <li>• Condition Engine Incidence</li>
                <li>• Dispute/Termination Rate</li>
                <li>• Closure Efficiency</li>
              </ul>
            </div>
          </div>
          
          <div className="pt-4 border-t border-white/10">
            <h4 className="font-medium text-foreground mb-2">Enforcement Progression</h4>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="border-red-600/30 text-red-400">
                SHADOW → Internal only
              </Badge>
              <span>→</span>
              <Badge variant="outline" className="border-yellow-600/30 text-yellow-400">
                SOFT → Show guidance, no constraints
              </Badge>
              <span>→</span>
              <Badge variant="outline" className="border-green-600/30 text-green-400">
                ENFORCED → Active governance
              </Badge>
            </div>
            <p className="mt-2 text-xs">
              Requires ≥65% accuracy + sufficient samples (30+ for 30d, 50+ for 60d) across ALL engines.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ValidationPanel;
